/**
 * Client-side pre-submit PII detection for the chat widget.
 *
 * This is the FIRST of two layers: it catches an obvious personal identifier
 * before the question ever leaves the browser and coaches the visitor to remove
 * it. The server is the guarantee — it redacts every identifier before anything
 * is stored regardless (app/services/answer_service.py redact()), and the L0
 * screen refuses PII questions. These patterns intentionally mirror the
 * high-confidence server ones (SSN in any separator, email, phone) so the two
 * layers agree; the widget stays conservative to avoid nagging on legitimate
 * questions, and never blocks silently.
 */

// SSN with dash / dot / space separators, or a bare 9-digit run next to an SSN cue.
const SSN_SEP = /\b\d{3}[-.\s]\d{2}[-.\s]\d{4}\b/;
const SSN_BARE = /\b\d{9}\b/;
const SSN_CUE = /\b(?:ssn|social[\s-]?security|soc\.?\s?sec)\b/i;
const EMAIL = /\b[\w.+-]+@[\w-]+\.[\w.-]+\b/;
const PHONE = /\b(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/;
const CARD = /\b\d(?:[ -]?\d){12,15}\b/;

const EMAIL_ALL = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g;
const PHONE_ALL = /(?:\+?1[-.\s]?)?\(?\b\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g;
const digits10 = (s) => s.replace(/\D/g, '').slice(-10);

/**
 * `text` without the email addresses and phone numbers that appear in
 * `published` (the assistant's own replies, which end with the agency's
 * contact line): "Can I email my form to sfersconnect@sfgov.org?" asks about
 * the agency, it doesn't share the visitor's personal information.
 */
function withoutPublished(text, published) {
  if (!published) return text;
  const emails = new Set((published.match(EMAIL_ALL) || []).map((e) => e.toLowerCase()));
  const phones = new Set((published.match(PHONE_ALL) || []).map(digits10));
  return text
    .replace(EMAIL_ALL, (m) => (emails.has(m.toLowerCase()) ? '' : m))
    .replace(PHONE_ALL, (m) => (phones.has(digits10(m)) ? '' : m));
}

/**
 * Return a short, friendly reason string if the text contains high-confidence
 * PII the visitor should remove, else null. Contact details already shown in
 * `published` (the assistant's replies) don't count.
 */
export function detectPii(text, published = '') {
  if (!text) return null;
  text = withoutPublished(text, published);
  if (SSN_SEP.test(text) || (SSN_BARE.test(text) && SSN_CUE.test(text))) {
    return 'a Social Security number';
  }
  if (CARD.test(text)) return 'a card or account number';
  if (EMAIL.test(text)) return 'an email address';
  if (PHONE.test(text)) return 'a phone number';
  return null;
}

/** User-facing coaching message for a detected PII kind. */
export function piiWarning(kind) {
  return `It looks like your question includes ${kind}. For your privacy, `
    + `please remove it — I answer from published information and never need `
    + `personal details.`;
}
